// Downloads Controls Component
// Search bar, sort-by buttons, and tags section
// URL integrated - matches marketplace pattern exactly

import React, { useEffect, useCallback, useRef } from 'react';
import { DownloadsSearchBar } from './DownloadsSearchBar';
import { DownloadsSortByButtons } from './DownloadsSortByButtons';
import { DownloadsTagBadge } from './DownloadsTagBadge';
import { DownloadsTagSearch } from './DownloadsTagSearch';
import { eventUtils } from '../../../core/utils';
import './DownloadsControls.css';


export function DownloadsControls({ className = '', urlQuery, urlSort, urlTags, searchParams, setSearchParams }) {
  // Local state for immediate UI responsiveness
  const [localSearchTerm, setLocalSearchTerm] = React.useState('');
  const [sortBy, setSortBy] = React.useState(urlSort || '');
  const [selectedTags, setSelectedTags] = React.useState(urlTags || []);

  // Initialize from URL on mount (only once)
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setLocalSearchTerm(urlQuery || '');
      setSortBy(urlSort || '');
      setSelectedTags(urlTags || []);
    }
  }, [urlQuery, urlSort, urlTags, searchParams, setSearchParams]);

  // Debounced function to update URL from typing (250ms delay)
  const updateUrlFromTyping = useCallback((query) => {
    const newParams = new URLSearchParams(searchParams);
    if (query) {
      newParams.set('query', query);
    } else {
      newParams.delete('query');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Create debounced version of URL update function (250ms delay)
  const debouncedUrlUpdateRef = useRef(null);
  useEffect(() => {
    debouncedUrlUpdateRef.current = eventUtils.debounce(updateUrlFromTyping, 250);
  }, [updateUrlFromTyping]);

  // Helper function to call the debounced URL update
  const debouncedUrlUpdate = useCallback((...args) => {
    if (debouncedUrlUpdateRef.current) {
      debouncedUrlUpdateRef.current(...args);
    }
  }, []);

  const handleTagSelect = useCallback((tagValue) => {
    if (tagValue && !selectedTags.includes(tagValue)) {
      const newTags = [...selectedTags, tagValue];
      setSelectedTags(newTags);

      // Update URL immediately
      const newParams = new URLSearchParams(searchParams);
      if (newTags.length > 0) {
        newParams.set('tags', newTags.join(','));
      } else {
        newParams.delete('tags');
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedTags, searchParams, setSearchParams]);

  const handleTagRemove = useCallback((tagToRemove) => {
    const newTags = selectedTags.filter(tag => tag !== tagToRemove);
    setSelectedTags(newTags);

    // Update URL immediately
    const newParams = new URLSearchParams(searchParams);
    if (newTags.length > 0) {
      newParams.set('tags', newTags.join(','));
    } else {
      newParams.delete('tags');
    }
    setSearchParams(newParams, { replace: true });
  }, [selectedTags, searchParams, setSearchParams]);

  // Handle immediate input changes (for responsive typing)
  const handleSearchChange = useCallback((newTerm) => {
    // Update local state immediately for responsive UI
    setLocalSearchTerm(newTerm);

    // Debounce URL update only (search happens on explicit submit)
    debouncedUrlUpdate(newTerm);
  }, [debouncedUrlUpdate]);

  // Handle immediate sort changes (empty = no sort, pins first)
  const handleSortChange = useCallback((newSort) => {
    setSortBy(newSort || '');
    const newParams = new URLSearchParams(searchParams);
    if (newSort) {
      newParams.set('sort', newSort);
    } else {
      newParams.delete('sort');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className={`downloads-controls ${className}`}>
      {/* Row 1: Search bar */}
      <div className="downloads-controls__row">
        <div className="downloads-controls__row-content downloads-controls__search-wrap">
          <DownloadsSearchBar
            searchTerm={localSearchTerm}
            onSearchChange={handleSearchChange}
            onSubmit={() => {}}
            placeholder="Search downloads..."
          />
        </div>
      </div>

      {/* Row 2: Sort by */}
      <div className="downloads-controls__row">
        <i className="fa-solid fa-arrow-down-wide-short downloads-controls__row-icon" aria-hidden />
        <div className="downloads-controls__row-content">
          <DownloadsSortByButtons value={sortBy} onChange={handleSortChange} />
        </div>
      </div>

      {/* Row 3: Tags */}
      <div className="downloads-controls__row">
        <i className="fa-solid fa-tags downloads-controls__row-icon" aria-hidden />
        <div className="downloads-controls__row-content downloads-controls__tags-wrap">
          <DownloadsTagSearch
            onTagSelect={handleTagSelect}
            selectedTags={selectedTags}
            className="downloads-controls__tag-search"
          />
          <div className="downloads-controls__selected-tags">
            {selectedTags.map((tag) => {
              // Convert tag value to proper label (capitalize first letter, handle special cases)
              const formatTagLabel = (tagValue) => {
                // Handle special cases first
                const specialCases = {
                  'javascript': 'JavaScript',
                  'python': 'Python',
                  'react': 'React',
                  'node': 'Node.js',
                  'api': 'API',
                  'ui': 'UI/UX',
                };

                if (specialCases[tagValue]) {
                  return specialCases[tagValue];
                }

                // Default: capitalize first letter
                return tagValue.charAt(0).toUpperCase() + tagValue.slice(1);
              };

              return (
                <DownloadsTagBadge
                  key={tag}
                  tag={formatTagLabel(tag)}
                  onRemove={() => handleTagRemove(tag)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
